import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, MessageCircle, Share2 } from 'lucide-react';
import { PLATFORM_LABEL } from './labels';
import { track } from './track';

export const SITE = 'https://recouvro.com';

export const listingUrl = (l) => SITE + l.path;

// The message that goes with a shared link (WhatsApp, SMS…).
export const shareMessage = (l) => `${l.title} — annonce de ${l.account} sur ${PLATFORM_LABEL[l.platform]}, vue sur Recouvro`;

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Older browsers, or a page not allowed to use the clipboard API.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
    document.body.removeChild(area);
    return ok;
  }
}

// "Copié ✓" for a moment after a successful copy.
function useFlash() {
  const [on, setOn] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = () => {
    setOn(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOn(false), 1800);
  };
  return [on, flash];
}

const STYLES = {
  icon: 'shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50',
  'icon-sm': 'shrink-0 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-blue-700 hover:bg-gray-100',
  button: 'flex items-center justify-center gap-2 w-full rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium px-4 py-2 hover:bg-gray-50',
  link: 'inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-700',
};

export function CopyButton({ text, label, copiedLabel = 'Copié', variant = 'button', title, onCopy }) {
  const [copied, flash] = useFlash();
  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (await copyText(text)) {
      flash();
      if (onCopy) onCopy();
    }
  };
  const small = variant === 'link' || variant === 'icon-sm';
  const icon = copied ? <Check size={small ? 12 : 16} className="text-green-600" /> : <Copy size={small ? 12 : 16} />;
  const iconOnly = variant === 'icon' || variant === 'icon-sm';
  return (
    <button type="button" onClick={click} className={STYLES[variant]} title={copied ? `${copiedLabel} ✓` : (title || label)} aria-label={title || label}>
      {icon}
      {!iconOnly && <span>{copied ? `${copiedLabel} ✓` : label}</span>}
    </button>
  );
}

// Phones: the system share sheet (WhatsApp, Messenger, SMS…). Elsewhere: copy the link.
export function ShareButton({ listing, variant = 'button' }) {
  const [copied, flash] = useFlash();
  const url = listingUrl(listing);
  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    track('share', listing);
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.title, text: shareMessage(listing), url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return; // the viewer closed the share sheet
      }
    }
    if (await copyText(`${shareMessage(listing)}\n${url}`)) flash();
  };
  const icon = copied ? <Check size={16} className="text-green-600" /> : <Share2 size={16} />;
  return (
    <button type="button" onClick={click} className={STYLES[variant]} title="Partager l'annonce" aria-label="Partager l'annonce">
      {icon}
      {variant !== 'icon' && <span>{copied ? 'Lien copié ✓' : 'Partager'}</span>}
    </button>
  );
}

// Opens WhatsApp with a ready message to the agent (built by the backend: the listing, the
// original post, our link and a 👍 for Recouvro). Only shown when a number is known.
export function WhatsAppButton({ listing, size = 'md', className = '' }) {
  if (!listing.whatsapp_url) return null;
  const pad = size === 'lg' ? 'px-4 py-3 font-semibold' : 'px-3 py-2 text-sm font-medium';
  return (
    <a
      href={listing.whatsapp_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track('whatsapp', listing)}
      className={`flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white rounded-lg ${pad} ${className}`}
    >
      <MessageCircle size={size === 'lg' ? 18 : 16} /> Écrire sur WhatsApp
    </a>
  );
}

// Any recouvro link (an agent's page, a search): share sheet on phones, else copy the link.
export function ShareLinkButton({ url, title, text, label = 'Partager', className = STYLES.button }) {
  const [copied, flash] = useFlash();
  const click = async (e) => {
    e.preventDefault();
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    if (await copyText(`${text}\n${url}`)) flash();
  };
  return (
    <button type="button" onClick={click} className={className} title={label} aria-label={label}>
      {copied ? <Check size={16} className="text-green-600" /> : <Share2 size={16} />}
      <span>{copied ? 'Lien copié ✓' : label}</span>
    </button>
  );
}
